import { motion } from "framer-motion";
import { Lightning, Envelope, CheckCircle, XCircle, SpinnerGap, X } from "@phosphor-icons/react";

export default function BatchProgress({ progress, onDismiss }) {
  const isRunning = progress.status === "running";
  const isDone = progress.status === "done";
  const isError = progress.status === "error";

  let bgColor = "bg-[--primary-light] border-[--primary]";
  let textColor = "text-[--primary]";
  let Icon = SpinnerGap;

  if (isDone) {
    bgColor = "bg-[--success-bg] border-[--success]";
    textColor = "text-[#008A39]";
    Icon = CheckCircle;
  } else if (isError) {
    bgColor = "bg-[--danger-bg] border-[--danger]";
    textColor = "text-[--danger]";
    Icon = XCircle;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`${bgColor} border-b px-6 py-3`}
      data-testid="batch-progress-banner"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon
            size={16}
            className={`${textColor} ${isRunning ? "animate-spin" : ""}`}
            weight={isRunning ? "regular" : "fill"}
          />
          <span className={`text-sm font-medium ${textColor}`}>
            {progress.message}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Show result summary if done */}
          {isDone && progress.results && (
            <span className="text-xs text-[--text-muted]">
              {progress.results.filter(r => r.status === "completed").length} succeeded
              {progress.results.filter(r => r.status === "failed").length > 0 && (
                <>, {progress.results.filter(r => r.status === "failed").length} failed</>
              )}
              {progress.results.filter(r => r.status === "skipped").length > 0 && (
                <>, {progress.results.filter(r => r.status === "skipped").length} skipped</>
              )}
            </span>
          )}
          {!isRunning && (
            <button
              onClick={onDismiss}
              className={`text-xs ${textColor} hover:underline font-medium`}
              data-testid="dismiss-batch-progress"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>

      {/* Detailed results for completed batch */}
      {isDone && progress.results && progress.results.length > 0 && (
        <div className="mt-2 max-h-32 overflow-y-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1">
            {progress.results.map((r, i) => (
              <div
                key={i}
                className="flex items-center gap-1.5 text-[11px] py-0.5"
                data-testid={`batch-result-${i}`}
              >
                {r.status === "completed" ? (
                  <CheckCircle size={12} weight="fill" className="text-[--success] flex-shrink-0" />
                ) : r.status === "failed" ? (
                  <XCircle size={12} weight="fill" className="text-[--danger] flex-shrink-0" />
                ) : (
                  <span className="w-3 h-3 rounded-full bg-[--text-muted] flex-shrink-0 opacity-40" />
                )}
                <span className="truncate text-[--text-secondary]">
                  {r.name}
                  {r.score !== undefined && <span className="ml-1 font-bold">{r.score}</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}
